import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { buildGenerationHeatmap, computeCOI } from "@/lib/coi";
import { buildConditionsDensity, buildProgenyConditionsSummary } from "@/lib/conditions";
import { QUALITIES, QUALITY_COLOR, BODY_PARTS } from "@/lib/quality";

const Q_ORDER = ["correct", "improve", "faulty", "serious", "unknown"];
const Q_LABEL = Object.fromEntries(QUALITIES.map((q) => [q.value, q.label]));

const Cell = ({ q, n, slots }) => {
  if (n === 0 || q === "unknown") {
    return <div className="h-7 rounded-sm border border-[var(--app-border)] bg-[var(--app-bg)]" title={`${slots} slot${slots === 1 ? "" : "s"} · no data`} />;
  }
  const ratio = Math.min(1, n / Math.max(1, slots));
  return (
    <div
      className="h-7 rounded-sm border border-black/10 flex items-center justify-center text-[10px] font-mono-tech text-white"
      style={{ backgroundColor: QUALITY_COLOR[q], opacity: 0.4 + 0.6 * ratio }}
      title={`${Q_LABEL[q]} · ${n}/${slots}`}
    >
      {n}
    </div>
  );
};

export default function AnalysisPanel({ pedigree, ancestorsKeyed, descendants, descendantsGenerations = 3, onDescendantsGenerationsChange }) {
  const totalGens = pedigree?.generations || 4;
  const ancestors = useMemo(() => {
    const out = {};
    Object.entries(pedigree?.ancestors || {}).forEach(([k, v]) => { out[String(k)] = v; });
    return out;
  }, [pedigree]);

  const heatmap = useMemo(() => buildGenerationHeatmap(ancestors, totalGens), [ancestors, totalGens]);
  const density = useMemo(() => buildConditionsDensity(ancestors, totalGens), [ancestors, totalGens]);
  const progenySummary = useMemo(() => buildProgenyConditionsSummary(descendants || []), [descendants]);
  const coi = useMemo(() => computeCOI(ancestors), [ancestors]);

  if (!pedigree) return <div className="text-sm text-[var(--app-text-soft)]">Open the Pedigree tab first to load ancestor data.</div>;

  const filledCount = heatmap.reduce((s, row) => s + row.filled, 0);
  const totalSlots = heatmap.reduce((s, row) => s + row.slots, 0);

  return (
    <div className="space-y-6" data-testid="analysis-panel">
      <div className="bg-white border border-[var(--app-border)] rounded-md p-4" data-testid="coi-card">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)]">
            Wright&apos;s Coefficient of Inbreeding (COI)
          </div>
          <div className="text-[10px] text-[var(--app-text-soft)] font-mono-tech">
            Across {totalGens} loaded generations
          </div>
        </div>
        <div className="flex items-baseline gap-3">
          <div
            className="font-display font-extrabold text-3xl text-[var(--app-text)]"
            data-testid="coi-value"
          >
            {(coi.coi * 100).toFixed(2)}%
          </div>
          <div className="text-xs text-[var(--app-text-soft)] leading-snug">
            Probability that two genes at any locus are identical by descent. <br />
            Higher COI ⇒ more inbreeding. Load more generations on the Pedigree tab for a deeper calculation.
          </div>
        </div>
      </div>

      <div className="bg-white border border-[var(--app-border)] rounded-md p-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)]">
            Structure heatmap — generation × part
          </div>
          <div className="text-[10px] text-[var(--app-text-soft)] font-mono-tech">
            {filledCount}/{totalSlots} ancestors filled
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-separate border-spacing-1" data-testid="heatmap-table">
            <thead>
              <tr>
                <th className="text-left text-[10px] uppercase tracking-wider text-[var(--app-text-soft)] font-semibold w-20">Gen</th>
                {BODY_PARTS.map((bp) => (
                  <th key={bp.key} className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)] font-semibold">{bp.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {heatmap.map((row) => (
                <tr key={row.gen}>
                  <td className="text-left font-display font-semibold text-xs">{row.label}</td>
                  {BODY_PARTS.map((bp) => {
                    const counts = row.counts[bp.key] || {};
                    const dom = row.dominant[bp.key];
                    if (dom === "mixed") {
                      // Split bar: render the top two qualities side by side
                      const ranked = Q_ORDER
                        .filter((q) => q !== "unknown")
                        .map((q) => ({ q, n: counts[q] || 0 }))
                        .filter((x) => x.n > 0)
                        .sort((a, b) => b.n - a.n);
                      return (
                        <td key={bp.key}>
                          <div className="grid grid-cols-2 gap-0.5">
                            {ranked.slice(0, 2).map((x) => <Cell key={x.q} q={x.q} n={x.n} slots={row.slots} />)}
                          </div>
                        </td>
                      );
                    }
                    return <td key={bp.key}><Cell q={dom} n={counts[dom] || 0} slots={row.slots} /></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-[var(--app-text-soft)]">
          {QUALITIES.filter((q) => q.value !== "unknown").map((q) => (
            <span key={q.value} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm border border-black/10" style={{ backgroundColor: q.color }} />
              {q.label}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[var(--app-border)] rounded-md p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3">
          Conditions density — per generation
        </div>
        <table className="w-full text-xs border-separate border-spacing-1" data-testid="conditions-density-table">
          <thead>
            <tr>
              <th className="text-left text-[10px] uppercase tracking-wider text-[var(--app-text-soft)] font-semibold w-44">Field</th>
              {density[0]?.cells.map((c) => (
                <th key={c.gen} className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)] font-semibold">
                  {c.gen === 0 ? "Subj" : `G${c.gen}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {density.map((row) => (
              <tr key={row.field}>
                <td className="text-left">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full border border-black/15" style={{ backgroundColor: row.color }} />
                    <span className="font-display font-semibold">{row.label}</span>
                  </span>
                </td>
                {row.cells.map((c) => {
                  const ratio = c.slots ? c.count / c.slots : 0;
                  // Background opacity 0.55..1 so the white count text stays readable.
                  const bgOpacity = c.count > 0 ? 0.55 + 0.45 * ratio : 0;
                  return (
                    <td key={c.gen}>
                      <div
                        className="h-7 rounded-sm border border-black/10 flex items-center justify-center text-[11px] font-mono-tech"
                        style={{
                          backgroundColor: c.count > 0
                            ? `color-mix(in srgb, ${row.color} ${Math.round(bgOpacity * 100)}%, white)`
                            : "var(--app-bg)",
                          color: c.count > 0 ? "#fff" : "var(--app-text-soft)",
                        }}
                        title={c.items.map((it) => `${it.name}: ${it.value}`).join("\n") || `0/${c.slots}`}
                      >
                        {c.count > 0 ? `${c.count}/${c.slots}` : "—"}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProgenyConditionsSection
        summary={progenySummary}
        generations={descendantsGenerations}
        onGenerationsChange={onDescendantsGenerationsChange}
        descendantsCount={(descendants || []).length}
      />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Progeny conditions — repeating genetic / medical / standard issues
 *   surfaced across every descendant down to N generations. Lets a breeder
 *   spot patterns that don't appear on the animal's own record.
 * ------------------------------------------------------------------------ */
function ProgenyConditionsSection({ summary, generations, onGenerationsChange, descendantsCount }) {
  const totalRepeating = summary.reduce(
    (acc, s) => acc + s.tokens.filter((t) => t.count >= 2).length,
    0,
  );
  const anyTokens = summary.some((s) => s.tokens.length > 0);

  const depthLabel = (d) => {
    if (d === 1) return "Child";
    if (d === 2) return "Grandchild";
    if (d === 3) return "Gt-grandchild";
    return `G${d}`;
  };

  return (
    <div className="bg-white border border-[var(--app-border)] rounded-md p-4" data-testid="progeny-conditions">
      <div className="flex items-baseline justify-between mb-1 gap-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)]">
          Progeny conditions — repeating issues
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">
          <span>Depth</span>
          <select
            className="border border-[var(--app-border)] rounded px-1.5 py-0.5 text-[11px] font-mono-tech bg-white"
            value={generations}
            onChange={(e) => onGenerationsChange && onGenerationsChange(parseInt(e.target.value, 10))}
            data-testid="progeny-depth-select"
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>{n} gen{n === 1 ? "" : "s"}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="text-[11px] text-[var(--app-text-soft)] mb-3" data-testid="progeny-summary-stats">
        Surveying <span className="font-mono-tech text-[var(--app-text)]">{descendantsCount}</span>{" "}
        descendant{descendantsCount === 1 ? "" : "s"} ·{" "}
        <span className="font-mono-tech text-[var(--app-text)]">{totalRepeating}</span>{" "}
        condition{totalRepeating === 1 ? "" : "s"} repeating across &gt;1 progeny
      </div>

      {!anyTokens ? (
        <div className="text-sm text-[var(--app-text-soft)] py-6 text-center border border-dashed border-[var(--app-border)] rounded">
          {descendantsCount === 0
            ? "No progeny recorded — record breedings to see hereditary trends here."
            : "No conditions noted on any descendant's record."}
        </div>
      ) : (
        <div className="space-y-4">
          {summary.map((s) => (
            <div key={s.field} data-testid={`progeny-field-${s.field}`}>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="w-3 h-3 rounded-full border border-black/15"
                  style={{ backgroundColor: s.color }}
                />
                <span className="font-display font-semibold text-sm">{s.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)] font-mono-tech">
                  {s.uniqueAffected}/{s.totalProgeny} affected
                </span>
              </div>
              {s.tokens.length === 0 ? (
                <div className="text-xs text-[var(--app-text-soft)] pl-5">None noted in progeny.</div>
              ) : (
                <ul className="space-y-2 pl-5" data-testid={`progeny-tokens-${s.field}`}>
                  {s.tokens.map((t) => (
                    <li key={t.token} className="border-l-2 pl-3" style={{ borderColor: s.color }}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className="font-display font-bold text-[15px] capitalize"
                          style={{ color: s.color }}
                          title={t.token}
                        >
                          {t.token}
                        </span>
                        <span className="text-[11px] font-mono-tech text-[var(--app-text-soft)]">
                          × {t.count} progeny
                        </span>
                        {t.count >= 2 && (
                          <span
                            className="text-[9px] uppercase tracking-[0.18em] font-display font-bold px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: s.color, color: "#fff" }}
                          >
                            Repeating
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {t.carriers.map((c) => (
                          <Link
                            key={c.id}
                            to={`/animals/${c.id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--app-bg)] border border-[var(--app-border)] text-[11px] hover:bg-white hover:shadow-sm transition-shadow"
                            data-testid={`progeny-carrier-${c.id}`}
                            title={c.originalText}
                          >
                            <span className="font-mono-tech text-[9px] uppercase tracking-wider text-[var(--app-text-soft)]">
                              {depthLabel(c.depth)}
                            </span>
                            <span className="font-display font-semibold">{c.name}</span>
                          </Link>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
